import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AspectRatioContainer,
  Breakpoints,
  Carousel,
  css,
  FloatingButtonWrapper,
  Lentils,
  MediaGalleryArrowButtons,
  MediaGalleryContextProvider,
  MediaGalleryFullscreenButton,
  MediaGallerySlide,
  Spinner,
  styled,
  Text,
  TextAlignment,
  TextTag,
  TokenTextAppearance,
  useMediaGalleryContext,
} from '@volkswagen-onehub/components-core';
import type { AspectRatioValue } from '@volkswagen-onehub/components-core';
import { minimalScreenWidth } from '@/common/breakpoints';
import { Navigation } from '@/enums/Navigation';
import { useTrackingService } from '@/contexts/TrackingServiceContext';
import { TrackingEvent } from '@/util/tracking/definitions';
import { GALLERY_DATA } from '../clusterButton/ClusterButton.types';
import { CLUSTER_BUTTON_TYPES } from '@/enums/ClusterButton';
import { GalleryThumbnails } from './galleryThumbnails';
import { normalizeIndex, shouldLoadGalleryImage } from './gallery.utils';
import { useMediaGalleryDesktopDrag } from './useMediaGalleryDesktopDrag';
import { ClusteredButtonContainer } from '../clusterButton/ClusterButtonContainer';

export type GalleryProps = {
  assets?: string[];
  labelPrefix: string;
  name: CLUSTER_BUTTON_TYPES;
  displaySubTitle?: boolean;
  tabData?: GALLERY_DATA[];
  displayTabs?: boolean;
  onImageClick?: (src: string, index: number) => void;
  sliderPadding?: string;
  aspectRatio?: AspectRatioValue;
  showFullscreenButton?: boolean;
  showThumbnails?: boolean;
  showLentils?: boolean;
};

type MediaGalleryTrackingProps = {
  itemCount: number;
  onSlideChange: (index: number) => void;
};

type MediaGalleryDesktopDragStageProps = {
  children: ReactNode;
  disabled?: boolean;
  stageRef: RefObject<HTMLDivElement | null>;
};

type GalleryStageImageProps = {
  src: string;
  alt: string;
  index: number;
  imageCount: number;
};

const MOBILE_GALLERY_BREAKPOINT = Breakpoints.b960;
const MEDIUM_GALLERY_SIDE_SPACE = 'clamp(48px, 7.5vw, 160px)';

const carouselBorderRadius = {
  [Breakpoints.default]: false,
  [Breakpoints.b960]: true,
};

const StyledGalleryContainer = styled.div`
  margin-bottom: ${(props) => props.theme.size.dynamic0100};
`;

const StyledGallery = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledSubTitleWrapper = styled(Text)`
  width: 100%;
  box-sizing: border-box;
  padding-top: ${(props) => props.theme.size.static150};
  padding-bottom: ${(props) => props.theme.size.dynamic0130};

  @media (max-width: ${MOBILE_GALLERY_BREAKPOINT - 1}px) {
    padding-right: ${(props) => props.theme.size.static200};
    padding-left: ${(props) => props.theme.size.static200};
  }

  @media screen and ${minimalScreenWidth.m} {
    padding-top: ${(props) => props.theme.size.dynamic0270};
  }

  @media screen and ${minimalScreenWidth.l} {
    padding-top: ${(props) => props.theme.size.dynamic0270};
  }

  @media screen and ${minimalScreenWidth.xxl} {
    padding-top: ${(props) => props.theme.size.dynamic0270};
  }
`;

const StyledSliderWrapper = styled.div<{ $hasCustomSliderPadding: boolean }>`
  width: 100%;
  box-sizing: border-box;

  ${({ $hasCustomSliderPadding }) =>
    !$hasCustomSliderPadding
      ? css`
          @media (min-width: ${Breakpoints.b960}px) and (max-width: ${Breakpoints.b1920 - 1}px) {
            padding-left: ${MEDIUM_GALLERY_SIDE_SPACE};
            padding-right: ${MEDIUM_GALLERY_SIDE_SPACE};
          }

          @media (min-width: ${Breakpoints.b1920}px) {
            padding-left: 0;
            padding-right: 0;
          }
        `
      : undefined};

  @media (max-width: ${MOBILE_GALLERY_BREAKPOINT - 1}px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const StyledMediaGalleryFrame = styled.div<{ $sliderPadding?: string }>`
  width: 100%;
  box-sizing: border-box;

  ${({ $sliderPadding }) =>
    $sliderPadding !== undefined
      ? css`
          padding: ${$sliderPadding};
        `
      : undefined};

  @media (max-width: ${MOBILE_GALLERY_BREAKPOINT - 1}px) {
    overflow: hidden;
    border-radius: 0;
    background-color: ${({ theme }) =>
      theme.variant === 'v26' ? theme.colors.background.tertiary : theme.colors.background.primary};
  }
`;

const StyledStageMediaLayout = styled.div<{ $isDraggable: boolean }>`
  position: relative;
  width: 100%;
  touch-action: pan-y;
  cursor: ${({ $isDraggable }) => ($isDraggable ? 'grab' : 'default')};

  &:active {
    cursor: ${({ $isDraggable }) => ($isDraggable ? 'grabbing' : 'default')};
  }

  &:fullscreen,
  &:-webkit-full-screen {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100vw;
    height: 100vh;
    max-width: none;
    padding: 0;
    cursor: default;
    touch-action: none;
    background-color: ${({ theme }) => theme.colors.background.primary};
  }

  @media (max-width: ${MOBILE_GALLERY_BREAKPOINT - 1}px) {
    cursor: default;

    &:active {
      cursor: default;
    }
  }
`;

const StyledAspectRatioContainer = styled(AspectRatioContainer)`
  ${StyledStageMediaLayout}:fullscreen &,
  ${StyledStageMediaLayout}:-webkit-full-screen & {
    height: 100vh !important;
    padding-bottom: 0 !important;
  }
`;

const StyledFullscreenButtonWrapper = styled(FloatingButtonWrapper)`
  position: absolute;
  display: block;
  top: ${({ theme }) => theme.size.static200};
  z-index: 4;

  ${({ theme }) =>
    theme.direction === 'rtl'
      ? `left: ${theme.size.static200};`
      : `right: ${theme.size.static200};`}
`;

const ControlsSpacer = styled.div`
  min-width: 0;
`;

const StyledMediaGalleryBottomControls = styled.div`
  z-index: 2;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: auto;
  box-sizing: border-box;
  gap: ${({ theme }) => theme.size.static150};
  padding: ${({ theme }) => `${theme.size.static100} ${theme.size.static250}`};
  background-color: ${({ theme }) =>
    theme.variant === 'v26' ? theme.colors.background.tertiary : theme.colors.background.primary};
  pointer-events: auto;

  ${ControlsSpacer} {
    display: none;
  }

  @media (min-width: ${MOBILE_GALLERY_BREAKPOINT}px) {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: end;
    gap: ${({ theme }) => theme.size.static150};
    min-height: auto;
    padding: ${({ theme }) => `${theme.size.static250} ${theme.size.static250}`};
    background-color: transparent;
    pointer-events: none;

    ${ControlsSpacer} {
      display: block;
    }
  }
`;

const StyledLentils = styled(Lentils)`
  ${({ theme }) => css`
    justify-self: start;
    align-self: center;
    background-color: transparent;
    pointer-events: auto;

    @media (min-width: ${MOBILE_GALLERY_BREAKPOINT}px) {
      justify-self: center;
      background-color: ${theme.variant === 'v26'
        ? theme.colors.background.tertiary
        : theme.colors.background.primary};
    }
  `};
`;

const StyledClusterControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  align-self: center;
  min-width: 0;
  margin-left: auto;

  @media (min-width: ${MOBILE_GALLERY_BREAKPOINT}px) {
    margin-left: 0;
  }
`;

const StyledClusteredButtonContainer = styled(ClusteredButtonContainer)`
  && {
    display: flex;
    align-items: center;
    justify-self: end;
    background-color: transparent;
    border-radius: 0;
    padding: 0;
    pointer-events: auto;
  }

  @media (min-width: ${MOBILE_GALLERY_BREAKPOINT}px) {
    && {
      background-color: ${({ theme }) =>
        theme.variant === 'v26'
          ? theme.colors.background.tertiary
          : theme.colors.background.primary};
      border-radius: ${({ theme }) => theme.size.static150};
      padding: ${({ theme }) => `${theme.size.static150} ${theme.size.static200}`};
    }
  }
`;

const StyledImageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  pointer-events: auto;
`;

const StyledStageImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const StyledGalleryImage = styled.img<{ $isLoaded: boolean }>`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0)};
  transition: opacity 0.2s ease;
  pointer-events: auto;
  user-select: none;
  -webkit-user-drag: none;
`;

const StyledImageSpinnerWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const getInitialActiveTab = (tabData?: GALLERY_DATA[]) => {
  return (
    tabData?.find((data) => data.images?.length)?.name ??
    tabData?.[0]?.name ??
    CLUSTER_BUTTON_TYPES.UNKNOWN
  );
};

const getActiveAssets = (
  activeTab: CLUSTER_BUTTON_TYPES,
  tabData?: GALLERY_DATA[],
  assets?: string[],
) => {
  const activeTabAssets = tabData?.find((data) => data.name === activeTab)?.images;

  if (activeTabAssets?.length) {
    return activeTabAssets;
  }

  const firstTabAssets = tabData?.find((data) => data.images?.length)?.images;

  return firstTabAssets?.length ? firstTabAssets : (assets ?? []);
};

const GalleryStageImage = ({
  src,
  alt,
  index,
  imageCount,
}: GalleryStageImageProps): ReactElement => {
  const {
    state: { activeIndex, lastIndex, slideLength },
  } = useMediaGalleryContext();

  const shouldLoadImage = shouldLoadGalleryImage({
    imageIndex: index,
    activeIndex,
    lastIndex,
    slideLength: slideLength || imageCount,
    preloadRange: 1,
  });

  const [hasRequestedImage, setHasRequestedImage] = useState(() => index === 0);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (shouldLoadImage) {
      setHasRequestedImage(true);
    }
  }, [shouldLoadImage]);

  useEffect(() => {
    setIsLoaded(false);
  }, [src]);

  return (
    <StyledStageImageWrapper>
      {hasRequestedImage && (
        <StyledGalleryImage
          src={src}
          alt={alt}
          draggable={false}
          loading={index === 0 ? 'eager' : 'lazy'}
          decoding="async"
          $isLoaded={isLoaded}
          onLoad={() => setIsLoaded(true)}
        />
      )}

      {shouldLoadImage && !isLoaded && (
        <StyledImageSpinnerWrapper>
          <Spinner variant="large" />
        </StyledImageSpinnerWrapper>
      )}
    </StyledStageImageWrapper>
  );
};

const MediaGalleryDesktopDragStage = ({
  children,
  disabled = false,
  stageRef,
}: MediaGalleryDesktopDragStageProps): ReactElement => {
  const desktopDragHandlers = useMediaGalleryDesktopDrag({
    disabled,
  });

  return (
    <StyledStageMediaLayout ref={stageRef} $isDraggable={!disabled} {...desktopDragHandlers}>
      {children}
    </StyledStageMediaLayout>
  );
};

const MediaGalleryTracking = ({
  itemCount,
  onSlideChange,
}: MediaGalleryTrackingProps): ReactElement | null => {
  const {
    state: { activeIndex, lastIndex, slideLength },
  } = useMediaGalleryContext();

  const isInitialRender = useRef(true);
  const lastTrackedIndex = useRef<number | null>(null);

  useEffect(() => {
    const length = slideLength || itemCount;

    if (!length) {
      return;
    }

    const stableIndex = Number.isInteger(activeIndex) ? activeIndex : lastIndex;
    const normalizedIndex = normalizeIndex(stableIndex, length);

    if (isInitialRender.current) {
      isInitialRender.current = false;
      lastTrackedIndex.current = normalizedIndex;
      return;
    }

    if (lastTrackedIndex.current === normalizedIndex) {
      return;
    }

    lastTrackedIndex.current = normalizedIndex;
    onSlideChange(normalizedIndex);
  }, [activeIndex, itemCount, lastIndex, onSlideChange, slideLength]);

  return null;
};

export const ImageGallery = (props: GalleryProps): ReactElement<GalleryProps> | null => {
  const {
    labelPrefix,
    name,
    displaySubTitle,
    tabData,
    assets,
    displayTabs,
    onImageClick,
    sliderPadding,
    aspectRatio = '16:9',
    showFullscreenButton = false,
    showThumbnails = false,
    showLentils = true,
  } = props;

  const [activeTab, setActiveTab] = useState<CLUSTER_BUTTON_TYPES>(() =>
    getInitialActiveTab(tabData),
  );

  const { t } = useTranslation();
  const trackingService = useTrackingService();
  const stageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const nextActiveTab = getInitialActiveTab(tabData);

    const currentTabStillExists = tabData?.some(
      (data) => data.name === activeTab && data.images?.length,
    );

    if (!currentTabStillExists && activeTab !== nextActiveTab) {
      setActiveTab(nextActiveTab);
    }
  }, [activeTab, tabData]);

  const imageAssets = useMemo(
    () => getActiveAssets(activeTab, tabData, assets),
    [activeTab, assets, tabData],
  );

  const galleryKey = useMemo(
    () => `${activeTab}-${imageAssets.length}-${imageAssets[0] ?? 'empty'}`,
    [activeTab, imageAssets],
  );

  const handleTabChange = useCallback((tabName: CLUSTER_BUTTON_TYPES) => {
    setActiveTab(tabName);
  }, []);

  const trackEvent = useCallback(
    (index: number) => {
      trackingService.track(TrackingEvent.VWBasicSliderElementLoad, {
        content: {
          Name: name,
          ItemCount: imageAssets.length,
          ItemPosition: index + 1,
        },
        eventInfo: {
          contentId: `trigger slider ${name}_${index}`,
        },
      });
    },
    [imageAssets.length, name, trackingService],
  );

  if (!imageAssets.length) {
    return null;
  }

  const hasSlides = imageAssets.length > 1;
  const hasTabs = Boolean(tabData && displayTabs);
  const hasCustomSliderPadding = sliderPadding !== undefined;
  const shouldShowLentils = hasSlides && showLentils;

  return (
    <StyledGalleryContainer id={Navigation.GALLERY} data-testid={`${name}-gallery-container`}>
      {displaySubTitle && (
        <StyledSubTitleWrapper
          tag={TextTag.h2}
          textAlign={TextAlignment.center}
          appearance={TokenTextAppearance.headline400}
          bold
          id={`${name}-gallery-description`}
        >
          {t(`body.details.gallery.${name}`)}
        </StyledSubTitleWrapper>
      )}

      <StyledGallery data-testid={`${name}-gallery`}>
        <MediaGalleryContextProvider key={galleryKey}>
          <StyledSliderWrapper $hasCustomSliderPadding={hasCustomSliderPadding}>
            <StyledMediaGalleryFrame $sliderPadding={sliderPadding}>
              <MediaGalleryDesktopDragStage disabled={!hasSlides} stageRef={stageRef}>
                <StyledAspectRatioContainer ratio={aspectRatio}>
                  <Carousel
                    hasBorderRadius={carouselBorderRadius}
                    role="region"
                    aria-label={labelPrefix}
                  >
                    {imageAssets.map((src, index) => (
                      <MediaGallerySlide key={`${src}-${index}`}>
                        {onImageClick ? (
                          <StyledImageButton
                            type="button"
                            data-gallery-image-button="true"
                            aria-label={`${labelPrefix} image ${index + 1}`}
                            onClick={() => onImageClick(src, index)}
                          >
                            <GalleryStageImage
                              src={src}
                              alt={`${labelPrefix} ${index + 1}`}
                              index={index}
                              imageCount={imageAssets.length}
                            />
                          </StyledImageButton>
                        ) : (
                          <GalleryStageImage
                            src={src}
                            alt={`${labelPrefix} ${index + 1}`}
                            index={index}
                            imageCount={imageAssets.length}
                          />
                        )}
                      </MediaGallerySlide>
                    ))}
                  </Carousel>
                </StyledAspectRatioContainer>

                {hasSlides && (
                  <MediaGalleryArrowButtons
                    ariaLabelPrev={`${labelPrefix} previous image`}
                    ariaLabelNext={`${labelPrefix} next image`}
                  />
                )}

                {showFullscreenButton && (
                  <StyledFullscreenButtonWrapper data-gallery-drag-ignore="true">
                    <MediaGalleryFullscreenButton
                      elementRef={stageRef}
                      size="small"
                      isInActiveAriaLabel={t('mediaGallery.enableFullscreen', 'Enable fullscreen')}
                      isActiveAriaLabel={t('mediaGallery.disableFullscreen', 'Disable fullscreen')}
                    />
                  </StyledFullscreenButtonWrapper>
                )}

                {(shouldShowLentils || hasTabs) && (
                  <StyledMediaGalleryBottomControls data-gallery-drag-ignore="true">
                    <ControlsSpacer />

                    {shouldShowLentils ? (
                      <StyledLentils
                        aria-label={`${labelPrefix} gallery slide`}
                        maxCount={{
                          [Breakpoints.default]: 5,
                          [Breakpoints.b560]: 7,
                        }}
                      />
                    ) : (
                      <ControlsSpacer />
                    )}

                    {hasTabs ? (
                      <StyledClusterControls data-testid="detail-gallery-clusterButtons">
                        <StyledClusteredButtonContainer
                          tabs={tabData ?? []}
                          activeTab={activeTab}
                          onTabChange={handleTabChange}
                        />
                      </StyledClusterControls>
                    ) : (
                      <ControlsSpacer />
                    )}
                  </StyledMediaGalleryBottomControls>
                )}
              </MediaGalleryDesktopDragStage>
            </StyledMediaGalleryFrame>
          </StyledSliderWrapper>

          {showThumbnails && <GalleryThumbnails assets={imageAssets} labelPrefix={labelPrefix} />}

          <MediaGalleryTracking itemCount={imageAssets.length} onSlideChange={trackEvent} />
        </MediaGalleryContextProvider>
      </StyledGallery>
    </StyledGalleryContainer>
  );
};
